import axios, { getData } from "../../../axios";
import { createSlice, createAction, PayloadAction } from "@reduxjs/toolkit";
import {
  decodeClientCargoTemplate,
  createClientCargoTemplateReqT,
  createClientCargoTemplateResT,
  editClientCargoTemplateReqT,
  getClientCargoTemplateResT,
} from "./types";
import { omit } from "lodash";
import { apiCargoTemplateToFormData, formDataStopToApiStop } from "./helpers";
import { notifyL } from "../../notifications";
import * as t from "io-ts";
import { createErrorReportingAsyncThunk } from "../../helpers";
import { CargoFormData } from "../cargo-dialog/types";
import { CargoFormDataBase } from "../cargo-and-template-shared";
import * as tPromise from "io-ts-promise";
import { AppDispatch, GetState } from "../../../redux-store";
import { defaultFormData } from "../schedule-cargo-template-dialog/helpers";
import { loadClientCargoTemplates } from "../../data/client-order-templates";
import {
  convertStopUnitsDecimalToString,
  ERR_DELETED_EXTRA_CO2_TYPES,
  ERR_OUTDATED_CARGO_REVISION,
} from "dora-shared";
import { isOutdatedRevisionError } from "../outdated-revision";
import { draftLinesCleared } from "../../data/draft-invoice-lines";
import {
  dropDeletedExtraCo2TypesParam,
  isDeletedExtraCo2TypesError,
} from "../../data/extra-co2-types";

export type ClientCargoTemplateFromApi = t.TypeOf<
  typeof getClientCargoTemplateResT
>;

const prefix = "app/client-cargo-template-dialog";

type CargoDialogStatus = "CREATE" | "EDIT" | "CLOSED";

interface CargoDialogState {
  status: CargoDialogStatus;
  initialFormData?: CargoFormData;
  resetFormData?: CargoFormData;
  previousFormData?: CargoFormData;
  isSaving: boolean;
  // The template open for editing. Its own id, not its cargo row's: that is
  // what its endpoints take (DORA-441).
  editingTemplateId: string | null;
  // The revision the open template is saved against (DORA-441).
  loadedDataRevisionNumber: string | null;
  // Someone else's change to the open template clashes with the user's
  // unsaved edits (DORA-441).
  remoteConflict: boolean;
}

const initialState: CargoDialogState = {
  status: "CLOSED",
  isSaving: false,
  editingTemplateId: null,
  loadedDataRevisionNumber: null,
  remoteConflict: false,
};

// The open template now stands at this revision (DORA-441).
export const openTemplateRevisionAdopted = createAction<string>(
  `${prefix}/open-template-revision-adopted`
);
export const openTemplateRemoteConflict = createAction(
  `${prefix}/open-template-remote-conflict`
);

export const cargoTemplateDeleted = createAction<{
  cargoTemplateId: string;
}>(`${prefix}/cargo-template-deleted`);

export const deleteCargoTemplate = createErrorReportingAsyncThunk(
  `${prefix}/delete`,
  async (
    data: {
      cargoTemplateId: string;
    },
    thunkApi
  ) => {
    const { cargoTemplateId } = data;
    await axios.delete<CargoFormData>(
      `/api/client-cargo-templates/${cargoTemplateId}`
    );
    thunkApi.dispatch({
      type: "DELETE_CARGO_TEMPLATE",
      payload: { cargoTemplateId },
    });
    thunkApi.dispatch(cargoTemplateDeleted({ cargoTemplateId }));
  }
);

export const loadCargoTemplate = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async (
    cargoTemplateId: string,
    thunkAPI
  ): Promise<{
    cargoTemplateId: string;
    data: CargoFormData;
    revision: string;
  }> => {
    try {
      const decoded = await axios
        .get(`/api/client-cargo-templates/${cargoTemplateId}`)
        .then(getData)
        .then(decodeClientCargoTemplate);
      return {
        cargoTemplateId,
        data: apiCargoTemplateToFormData(decoded),
        revision: decoded.data.revision,
      };
    } catch (e) {
      thunkAPI.dispatch(
        notifyL({
          namespace: "notifications",
          key: "loadingCargoTemplateFailed",
          type: "error",
        })
      );
      throw e;
    }
  }
);

// Someone else changed the open template: fetch the latest version for the
// dialog to merge into its form, leaving the dialog's state alone (DORA-441).
// Null when no template is open.
export const fetchLatestOpenTemplate = createErrorReportingAsyncThunk(
  `${prefix}/fetch-latest-open-template`,
  async (
    _: void,
    thunkAPI
  ): Promise<{ data: CargoFormData; revision: string } | null> => {
    const templateId =
      thunkAPI.getState().app.clientCargoTemplateDialog.editingTemplateId;
    if (!templateId) {
      return null;
    }
    const decoded = await axios
      .get(`/api/client-cargo-templates/${templateId}`)
      .then(getData)
      .then(decodeClientCargoTemplate);
    return {
      data: apiCargoTemplateToFormData(decoded),
      revision: decoded.data.revision,
    };
  },
  // A background fetch: a failure shouldn't toast over the user's work.
  { skipNotification: true }
);

export const submit = createErrorReportingAsyncThunk(
  `${prefix}/submit`,
  async (
    formData: CargoFormDataBase & {
      keepAlive?: boolean;
      submitType: "CREATE" | "UPDATE";
      cargoTemplateId?: string;
      cargoIdToCopyInvoiceLinesFrom: string | null;
      cargoIdToCopyFilesFrom: string | null;
      name?: string;
      dropDeletedExtraCo2Types?: boolean;
    },
    thunkAPI
  ) => {
    const actualFormData = omit(formData, [
      "submitType",
      "keepAlive",
      "dropDeletedExtraCo2Types",
    ]);
    const params = dropDeletedExtraCo2TypesParam(
      formData.dropDeletedExtraCo2Types
    );
    // Adding lines whose extra CO2 type was deleted is refused until the
    // user agrees to save without them; the dialog asks and resubmits.
    const refusal = (err: unknown) => {
      if (isDeletedExtraCo2TypesError(err)) {
        return {
          requestFormData: actualFormData,
          keepAlive: formData.keepAlive,
          submitType: formData.submitType,
          error: ERR_DELETED_EXTRA_CO2_TYPES,
        } as const;
      }
      // Someone else saved the template meanwhile: the dialog handles it.
      if (isOutdatedRevisionError(err)) {
        return {
          requestFormData: actualFormData,
          keepAlive: formData.keepAlive,
          submitType: formData.submitType,
          error: ERR_OUTDATED_CARGO_REVISION,
        } as const;
      }
      throw err;
    };

    const formDataFull = {
      ...actualFormData,
      minTemperature: formData.minTemperature || null,
      maxTemperature: formData.maxTemperature || null,
      type: formData.type,
      clientId: formData.clientId || null,
      pickupList: formData.pickupList.map(formDataStopToApiStop),
      dropoffList: formData.dropoffList.map(formDataStopToApiStop),
      stopUnits: formData.stopUnits,
    };
    let data;

    if (formData.submitType === "CREATE") {
      const invoiceLines = thunkAPI
        .getState()
        .data.draftInvoiceLines.lines.map((l) => ({
          productId: l.productId,
          unitId: l.unitId,
          description: l.description,
          quantity: l.quantity,
          amount: l.amount,
          discountPercentage: l.discountPercentage,
          percentage: l.percentage,
          isFromRoadToll: l.isFromRoadToll,
        }));
      const res = await axios
        .post(
          `/api/client-cargo-templates`,
          createClientCargoTemplateReqT.encode({
            name: formData.name!,
            cargoIdToCopyInvoiceLinesFrom:
              formData.cargoIdToCopyInvoiceLinesFrom,
            cargoIdToCopyFilesFrom: formData.cargoIdToCopyFilesFrom,
            data: {
              ...formDataFull,
              stopUnits: formData.stopUnits.map((su) => ({
                ...su,
                ...convertStopUnitsDecimalToString(su),
              })),
              invoiceLines,
            },
          }),
          { params }
        )
        .then(getData)
        .then(tPromise.decode(createClientCargoTemplateResT))
        .catch(refusal);
      if ("error" in res) {
        return res;
      }
      thunkAPI.dispatch(draftLinesCleared());
      thunkAPI.dispatch(loadClientCargoTemplates());
      return res;
    } else {
      // The revision the dialog loaded (DORA-441).
      const revision =
        thunkAPI.getState().app.clientCargoTemplateDialog
          .loadedDataRevisionNumber;
      const url = revision
        ? `/api/client-cargo-templates/${formData.cargoTemplateId}?revisionNumber=${revision}`
        : `/api/client-cargo-templates/${formData.cargoTemplateId}`;
      data = await axios
        .put(
          url,
          editClientCargoTemplateReqT.encode({
            id: formData.cargoTemplateId!,
            data: {
              ...formDataFull,
              stopUnits: formData.stopUnits.map((su) => ({
                ...su,
                ...convertStopUnitsDecimalToString(su),
              })),
              // No invoiceLines: an existing template's lines live on its
              // delivery and are saved line by line while editing. Sending
              // the (empty) draft lines here would wipe them server-side.
            },
          }),
          { params }
        )
        .then(getData)
        .catch(refusal);
      if (data && "error" in data) {
        return data;
      }
      // A dialog kept open (Ctrl+S) saves next against this revision.
      const saved = await tPromise.decode(t.type({ revision: t.string }))(data);
      thunkAPI.dispatch(openTemplateRevisionAdopted(saved.revision));
      thunkAPI.dispatch(
        notifyL({
          namespace: "notifications",
          key: "shipmentTemplateSaved",
          type: "success",
        })
      );
    }
    return {
      ...(data && {
        id: data?.data?.id || formData.cargoTemplateId,
      }),
      requestFormData: actualFormData,
      keepAlive: formData.keepAlive,
      submitType: formData.submitType,
    };
  }
);

export const createNewCargoTemplate =
  () => (dispatch: AppDispatch, getState: GetState) => {
    const state = getState().app.clientCargoTemplateDialog;
    const assignTeamId = getState().auth.user?.defaultTeamId;
    const newShipmentDateAdjustment =
      getState().data.orgSettings.newShipmentDate;
    const defaultData = defaultFormData({
      assignedTeamId: assignTeamId!,
      newShipmentDateAdjustment,
    });
    const initialFormData = state.previousFormData || defaultData;
    const resetFormData = defaultData;
    return dispatch(
      slice.actions.createNewCargoTemplate({
        initialFormData,
        resetFormData,
      })
    );
  };

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    createNewCargoTemplate: (
      state,
      action: PayloadAction<{
        initialFormData: CargoFormData;
        resetFormData: CargoFormData;
      }>
    ) => {
      state.status = "CREATE";
      state.initialFormData = action.payload.initialFormData;
      state.previousFormData = undefined;
      state.resetFormData = action.payload.resetFormData;
    },
    close: (state, action: PayloadAction<CargoFormData | undefined>) => {
      state.initialFormData =
        state.status === "EDIT" ? undefined : state.initialFormData;
      state.previousFormData =
        state.status === "CREATE" ? action.payload : undefined;
      state.status = "CLOSED";
      state.editingTemplateId = null;
      state.loadedDataRevisionNumber = null;
      state.remoteConflict = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submit.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(submit.fulfilled, (state, { payload }) => {
        state.isSaving = false;
        if ("error" in payload) {
          // refused, the dialog stays open and handles it
          return;
        }
        state.status = "CLOSED";
        state.previousFormData = undefined;
        state.initialFormData = undefined;
      })
      .addCase(submit.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(loadCargoTemplate.fulfilled, (state, action) => {
        state.initialFormData = action.payload.data;
        state.resetFormData = action.payload.data;
        state.status = "EDIT";
        // A reload of the open template (after Ctrl+S) keeps a clash flagged
        // meanwhile; another template starts without one (DORA-441).
        if (state.editingTemplateId !== action.payload.cargoTemplateId) {
          state.remoteConflict = false;
        }
        state.editingTemplateId = action.payload.cargoTemplateId;
        state.loadedDataRevisionNumber = action.payload.revision;
      })
      .addCase(openTemplateRevisionAdopted, (state, action) => {
        state.loadedDataRevisionNumber = action.payload;
      })
      .addCase(openTemplateRemoteConflict, (state) => {
        state.remoteConflict = true;
      })
      .addCase(deleteCargoTemplate.fulfilled, (state) => {
        state.status = "CLOSED";
        state.initialFormData = undefined;
        state.previousFormData = undefined;
        state.resetFormData = undefined;
      });
  },
});

export const { close } = slice.actions;
export default slice.reducer;
